/**
 * web/src/lib/actionFeedback.ts — one place where a failed in-app action
 * announces itself (UX review H7, card ESidDNa3mmaP).
 *
 * The catch blocks behind kudos, request transitions, date actions, the nudge
 * quick-picks, Notebook saves and question answers used to call only
 * `console.error`, so a failure looked exactly like a success: the button
 * returned to normal and the user was told nothing. Chat was worse — the
 * composer cleared itself before `onSend`, and a closed socket dropped the
 * text with no trace (see hooks/useThread.ts).
 *
 * The helper fires a CustomEvent — the same channel lib/api.ts already uses
 * for `pear:plus-required` — so any surface can listen without threading a
 * callback through every card. It carries HUMAN COPY ONLY: no API JSON and no
 * error codes, because the review's prescription is that ordinary copy never
 * shows them (the technical detail belongs in console.error, which every call
 * site keeps).
 */
export const ACTION_FAILED_EVENT = 'pear:action-failed';

/** Extra shape a failure notice can carry (card 4FFux4skKvYw). */
export interface ActionFailureOptions {
  /** A full sentence to show INSTEAD of "Couldn't <what> — try again."
   *  Use it when the outcome is not a plain failure — e.g. a message that was
   *  queued rather than lost. */
  message?: string;
  /** Auto-dismiss window for this notice. Defaults to the toast's own 8s. */
  ttlMs?: number;
  /** Makes the notice actionable: the label to render and the affordance it
   *  runs. A queued send offers Retry here, so the user never retypes. */
  retry?: { label: string; run: () => void };
}

/**
 * `what` is phrased as the thing that did not happen — `reportActionFailure(
 * 'send that message')` renders "Couldn't send that message — try again."
 */
export function reportActionFailure(what: string, opts: ActionFailureOptions = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACTION_FAILED_EVENT, { detail: { what, ...opts } }));
}
